/**
 * bloomShaders.ts — the SHARED bloom source of truth (BL-37b / S87).
 *
 * The WebGL2 post-FX (`WebGL2Effects`) bloomed only the arena canvas; its
 * shader strings + tuning numbers lived inline. BL-37b promotes the HUD chrome
 * to the SAME light language as the arena (ART §9: panels are *lit* — a hot
 * edge and a falloff, consistent with §6.1 emissive-core thinking) by running a
 * 2nd `WebGL2Effects` instance over the UI-chrome canvas. To keep the arena and
 * the UI on ONE bloom — not a fork — the GLSL below is lifted out of the class
 * **verbatim** (the arena pass is byte-identical) and the numeric constants are
 * gathered into one `BLOOM` block. Both instances compile these exact strings;
 * the UI instance only varies the *uniform values it passes* (threshold,
 * composite intensity, chroma) — never the shader source.
 *
 * `bloomShaders.test.ts` pins the GLSL literals + the constants so the shared
 * source can never silently drift.
 *
 * NOTE: extracted verbatim from the inline strings that previously lived in
 * `WebGL2Effects.ts`. Do not edit the GLSL here without re-pinning the test and
 * re-verifying the arena pass is byte-identical (the determinism baseline must
 * stay byte-unchanged).
 */

/** The shared bloom tuning. The arena passes these defaults; the UI instance
 *  overrides the *uniform values* (see `BloomConfig`) but never the GLSL. */
export const BLOOM = {
  /** Brightness above which a pixel contributes to bloom (the `u_threshold`
   *  the arena passes in `WebGL2Effects.render`). */
  THRESHOLD: 0.5,
  /** Rec.709 luminance weights — how "bright" a colour reads to the extract. */
  LUMA: [0.2126, 0.7152, 0.0722] as const,
  /** Separable Gaussian (5-tap, mirrored) — centre weight then ±1..±4. */
  GAUSS_WEIGHTS: [0.227027, 0.1945946, 0.1216216, 0.054054, 0.016216] as const,
  /** Composite gain applied to the blurred bloom before it is added back
   *  (the `* 0.6` in `BLOOM_COMPOSITE_FRAG`). */
  COMPOSITE_GAIN: 0.6,
  /** Arena composite intensity at the `high` / `medium` quality tiers. */
  INTENSITY_HIGH: 1.0,
  INTENSITY_MEDIUM: 0.5,
  /** Arena chromatic-aberration offset (the `high`-tier `u_offset`). */
  CHROMA_OFFSET: 0.003,
  /** Bloom blur runs at source ÷ this divisor (half-res for the arena, Q-020). */
  DOWNSCALE: 2,
} as const;

export const VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 a_position;
layout(location = 1) in vec2 a_texCoord;
out vec2 v_texCoord;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texCoord = a_texCoord;
}`;

export const BLOOM_EXTRACT_FRAG = `#version 300 es
precision mediump float;
in vec2 v_texCoord;
out vec4 fragColor;
uniform sampler2D u_texture;
uniform float u_threshold;
void main() {
  vec4 color = texture(u_texture, v_texCoord);
  float brightness = dot(color.rgb, vec3(0.2126, 0.7152, 0.0722));
  float contribution = max(0.0, brightness - u_threshold) / max(brightness, 0.001);
  fragColor = vec4(color.rgb * contribution, 1.0);
}`;

export const BLOOM_BLUR_FRAG = `#version 300 es
precision mediump float;
in vec2 v_texCoord;
out vec4 fragColor;
uniform sampler2D u_texture;
uniform vec2 u_direction;
void main() {
  vec3 result = vec3(0.0);
  float weights[5] = float[](0.227027, 0.1945946, 0.1216216, 0.054054, 0.016216);
  result += texture(u_texture, v_texCoord).rgb * weights[0];
  for (int i = 1; i < 5; i++) {
    vec2 off = u_direction * float(i) * 2.0;
    result += texture(u_texture, v_texCoord + off).rgb * weights[i];
    result += texture(u_texture, v_texCoord - off).rgb * weights[i];
  }
  fragColor = vec4(result, 1.0);
}`;

// Bloom-only output with luminance-derived alpha. The composite layer is
// drawImage'd over the 2D canvas — by emitting *only* the bloom contribution
// with alpha = bloom luminance, the composite blends additively over the
// existing 2D scene instead of overwriting it. Result: even if the WebGL
// pipeline is in an unstable state (context just restored, FBOs stale,
// shader compile glitch on some GPUs), at worst the bloom flickers — the
// underlying 2D scene stays visible. Previously fragColor.a=1.0 made the
// composite fully opaque so any glitched output would wipe the canvas black.
// This same alpha-awareness is why a transparent UI-chrome source composites
// cleanly over the DOM (BL-37b): empty chrome regions stay transparent.
export const BLOOM_COMPOSITE_FRAG = `#version 300 es
precision mediump float;
in vec2 v_texCoord;
out vec4 fragColor;
uniform sampler2D u_source;
uniform sampler2D u_bloom;
uniform float u_intensity;
void main() {
  vec4 bloom = texture(u_bloom, v_texCoord);
  vec3 contribution = bloom.rgb * u_intensity * 0.6;
  float a = clamp(max(contribution.r, max(contribution.g, contribution.b)), 0.0, 1.0);
  fragColor = vec4(contribution, a);
}`;

// Chromatic aberration + final composite. CRITICAL: alpha must be derived from
// luminance, NOT hard-coded to 1.0 — this shader's output is the WebGL canvas
// that gets drawImage'd over the 2D scene. With alpha=1.0 the dark areas of
// bloom (i.e. most of the screen) wipe the 2D scene to opaque black. Same
// principle as BLOOM_COMPOSITE_FRAG above.
export const CHROMA_FRAGMENT = `#version 300 es
precision mediump float;
in vec2 v_texCoord;
out vec4 fragColor;
uniform sampler2D u_texture;
uniform float u_offset;
void main() {
  float r = texture(u_texture, v_texCoord + vec2(u_offset, 0.0)).r;
  float g = texture(u_texture, v_texCoord).g;
  float b = texture(u_texture, v_texCoord - vec2(u_offset, 0.0)).b;
  float a = clamp(max(r, max(g, b)), 0.0, 1.0);
  fragColor = vec4(r, g, b, a);
}`;
